import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, BookOpenCheck, Loader2, Lock, RefreshCw, PenLine, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";

const ROUNDS = [["hr", "HR round"], ["legal", "Legal round"]];

export function BankHeader({ data, staff, onGenerate, busy }) {
  const has = data.questions.length > 0;
  return (
    <section className="panel flex flex-wrap items-start gap-4 p-5" data-tour="pt-bank-header" data-testid="bank-header">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-tint text-brand"><BookOpenCheck size={18} aria-hidden="true" /></div>
      <div className="min-w-[240px] flex-1 space-y-1">
        <h2 className="text-lg font-semibold">Interview question bank</h2>
        <p className="text-sm text-ink-secondary">Written by AI from this job description, split into an HR round and a Legal round. Each candidate hears a random set when you create their interview link.</p>
        {has && <p className="text-xs text-ink-muted" data-testid="bank-counts">{data.questions.length} questions · {data.counts.hr} HR · {data.counts.legal} Legal{data.removed ? ` · ${data.removed} removed` : ""}</p>}
      </div>
      {staff && (
        <Button onClick={() => onGenerate(has)} disabled={busy || data.status === "generating" || data.jd_words < data.min_words} variant={has ? "outline" : "default"}
          data-tour="pt-bank-generate-btn" data-testid={has ? "bank-regenerate-btn" : "bank-generate-btn"}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : has ? <RefreshCw size={16} /> : <PenLine size={16} />} {has ? "Regenerate" : "Generate question bank"}
        </Button>
      )}
    </section>
  );
}

export function BankNotices({ data, job, onRetry, staff }) {
  if (data.jd_words < data.min_words) return (
    <div role="alert" className="flex flex-wrap items-center gap-2 rounded-[6px] border border-[#E8D7B0] bg-caution-tint p-4 text-sm text-caution" data-testid="bank-short-jd">
      <AlertTriangle size={16} aria-hidden="true" /> Add more detail to the job description to generate questions
      <span className="text-xs">({data.jd_words} of {data.min_words} words)</span>
      {staff && <Link to={`/app/jobs/new?draft=${job.id}&step=1`} className="ml-auto font-medium underline">Edit description</Link>}
    </div>
  );
  if (data.status === "generating") return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="panel space-y-2 p-5" role="status" aria-live="polite" data-testid="bank-progress">
      <div className="flex items-center gap-2 text-sm font-medium"><Loader2 size={14} className="animate-spin text-brand" /> Writing questions from the job description…</div>
      <Progress value={data.progress?.percent ?? 5} className="h-2 bg-brand-tint" aria-label="Generation progress" />
      <p className="text-xs text-ink-muted" data-testid="bank-progress-message">{data.progress?.message || "Queued"} · usually under a minute. You can leave this page.</p>
    </motion.div>
  );
  if (data.status === "failed") return (
    <div role="alert" className="flex flex-wrap items-center gap-3 rounded-[6px] border border-[#E8C9C5] bg-risk-tint p-4 text-sm text-risk" data-testid="bank-error">
      <AlertTriangle size={16} aria-hidden="true" /> {data.error_message}
      {staff && <Button size="sm" variant="outline" className="ml-auto" onClick={onRetry} data-testid="bank-retry-btn">Retry</Button>}
    </div>
  );
  return null;
}

export function DrawSettings({ data, staff, onSave }) {
  const [d, setD] = useState(data.draw);
  const changed = d.hr !== data.draw.hr || d.legal !== data.draw.legal;
  const field = (k, label) => (
    <label className="flex items-center gap-2 text-sm">{label}
      <Input type="number" min={1} max={Math.min(15, data.counts[k] || 15)} value={d[k]} disabled={!staff} className="h-9 w-20"
        onChange={(e) => setD({ ...d, [k]: Number(e.target.value) })} data-testid={`bank-draw-${k}-input`} />
    </label>
  );
  return (
    <section className="panel flex flex-wrap items-center gap-4 p-5" data-tour="pt-bank-draw" data-testid="bank-draw">
      <div className="min-w-[200px] flex-1"><h3 className="text-sm font-semibold">Questions per candidate</h3><p className="text-xs text-ink-muted">A random, non-repeating set is drawn for each candidate and saved with their interview.</p></div>
      {field("hr", "HR")}{field("legal", "Legal")}
      {staff && <Button size="sm" disabled={!changed} onClick={() => onSave(d)} data-testid="bank-draw-save-btn">Save</Button>}
    </section>
  );
}

export function QuestionList({ data, staff, onRemove }) {
  const [round, setRound] = useState("legal");
  const qs = data.questions.filter((q) => q.round === round);
  return (
    <section className="panel overflow-hidden" data-tour="pt-bank-questions" data-testid="bank-questions">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-5 py-3" role="tablist" aria-label="Interview rounds">
        {ROUNDS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={round === k} onClick={() => setRound(k)}
            className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${round === k ? "bg-brand text-white" : "text-ink-secondary hover:bg-subtle"}`} data-testid={`bank-round-${k}`}>
            {label} ({data.counts[k]})
          </button>
        ))}
        <span className="ml-auto flex items-center gap-1 text-xs text-ink-muted" data-testid="bank-no-custom-note"><Lock size={12} aria-hidden="true" /> Questions can't be typed in — remove any you don't want, or regenerate.</span>
      </div>
      <ol className="divide-y divide-line">
        <AnimatePresence initial={false}>
          {qs.map((q, i) => (
            <motion.li key={q.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, height: 0 }} className="flex items-start gap-3 px-5 py-3" data-testid={`bank-q-${q.id}`}>
              <span className="w-6 pt-0.5 text-xs tabular-nums text-ink-muted">{i + 1}</span>
              <div className="min-w-0 flex-1 space-y-1"><p className="text-sm">{q.text}</p>{q.topic && <span className="inline-block rounded-full bg-subtle px-2 py-0.5 text-xs text-ink-secondary">{q.topic}</span>}</div>
              {staff && <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" aria-label={`Remove question ${i + 1}`} onClick={() => onRemove(q)} data-testid={`bank-remove-${q.id}`}><Trash2 size={14} /></Button>}
            </motion.li>
          ))}
        </AnimatePresence>
      </ol>
    </section>
  );
}
